import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { b2bRatesService } from '../services/b2bRates';

const keys = {
  all: ['b2b-admin-rates'] as const,
  plans: () => [...keys.all, 'plans'] as const,
  planRates: (planId: string | number) => [...keys.all, 'planRates', planId] as const,
};

export const useB2bRates = () => {
  const queryClient = useQueryClient();

  const useB2bPlansQuery = () => {
    return useQuery({
      queryKey: keys.plans(),
      queryFn: () => b2bRatesService.getAllPlans(),
      staleTime: 5 * 60 * 1000,
    });
  };

  const useB2bRatesQuery = (planId: string | number, options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: keys.planRates(planId),
      queryFn: () => b2bRatesService.getPlanRates(planId),
      enabled: !!planId && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000,
    });
  };

  const useUserB2bRateCardsQuery = (userId: string | number, options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: [...keys.all, 'userRateCards', userId],
      queryFn: () => b2bRatesService.getUserRateCards(userId),
      enabled: !!userId && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000,
    });
  };

  const useCreateB2bPlanMutation = () => {
    return useMutation({
      mutationFn: (name: string) => b2bRatesService.createPlan(name),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
      },
    });
  };

  const useUpdateB2bRatesMutation = () => {
    return useMutation({
      mutationFn: ({ planId, courierId, payload }: { planId: string | number; courierId: string | number; payload: any }) =>
        b2bRatesService.updatePlanRates(planId, courierId, payload),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useAddChargeMutation = () => {
    return useMutation({
      mutationFn: ({ planId, courierId, payload }: { planId: string | number; courierId: string | number; payload: any }) =>
        b2bRatesService.addCharge(planId, courierId, payload),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useUpdateChargeMutation = () => {
    return useMutation({
      mutationFn: ({ chargeId, planId, payload }: { chargeId: string | number; planId: string | number; payload: any }) =>
        b2bRatesService.updateCharge(chargeId, payload),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useDeleteChargeMutation = () => {
    return useMutation({
      mutationFn: ({ chargeId, planId }: { chargeId: string | number; planId: string | number }) =>
        b2bRatesService.deleteCharge(chargeId),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useUploadB2bRatesMutation = () => {
    return useMutation({
      mutationFn: ({ planId, file }: { planId: string | number; file: File }) =>
        b2bRatesService.uploadAndReplaceRates(planId, file),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useUploadNewB2bPlanMutation = () => {
    return useMutation({
      mutationFn: ({ planName, file }: { planName: string; file: File }) => b2bRatesService.uploadNewPlanRates(planName, file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
      },
    });
  };

  const downloadSampleB2bRates = async () => {
    try {
      const blob = await b2bRatesService.downloadSampleRates();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `b2b_sample_sheet.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download sample rates', error);
      throw error;
    }
  };

  return {
    useB2bPlansQuery,
    useCreateB2bPlanMutation,
    useB2bRatesQuery,
    useUserB2bRateCardsQuery,
    useUpdateB2bRatesMutation,
    useAddChargeMutation,
    useUpdateChargeMutation,
    useDeleteChargeMutation,
    useUploadB2bRatesMutation,
    useUploadNewB2bPlanMutation,
    downloadB2bRates: b2bRatesService.downloadRates,
    downloadSampleB2bRates,
  };
};
